<template>
  <div v-rect-loading="!hiddenLoading && loading" class="hm-selector-container">
    <div v-if="!hiddenHeaderMessage" class="message">{{ $t(`${i18nPrefix}.label.hiringManages`) }}</div>
    <div class="selector-content" :class="selectHmError ? 'has-hm-email-error' : ''">
      <div class="hm-email">
        <el-select
          v-model="hmEmail"
          filterable
          allow-create
          default-first-option
          autocomplete="new-password"
          :disabled="disableAddHm"
          :filter-method="filterMethod"
          :placeholder="$t(`${i18nPrefix}.placeholder.hiringEmails`)"
          @change="handleSelectHm"
          @visible-change="visibleChange"
        >
          <el-option
            v-for="(hmInfo, index) in filterHmEmailList"
            v-show="!hmInfo.isSelected"
            :key="hmInfo.id"
            :label="hmInfo.email_address"
            :value="hmInfo.value || hmInfo.email_address"
          >
            <slot name="hmOption" :hmInfo="hmInfo" :index="index"></slot>
          </el-option>
        </el-select>
        <div v-if="emailError" class="error">{{ emailError }}</div>
        <div v-if="selectHmError" class="error">{{ selectHmError }}</div>
      </div>
      <div class="hm-first-name">
        <el-input
          v-model="hmFirstName"
          autocomplete="new-password"
          :disabled="disableAddHm"
          :readonly="isSelectHmEmail"
          :placeholder="$t(`${i18nPrefix}.placeholder.hiringFirstName`)"
          >
        </el-input>
        <div v-if="firstNameError" class="error">{{ firstNameError }}</div>
      </div>
      <el-input
        v-model="hmLastName"
        class="hm-last-name"
        autocomplete="new-password"
        :disabled="disableAddHm"
        :readonly="isSelectHmEmail"
        :placeholder="$t(`${i18nPrefix}.placeholder.hiringLastName`)"
      >
      </el-input>
      <el-button class="add-hm-btn" type="text" :class="disableAddHm && 'is-disabled'" @click="handleAddHire">
        <i class="icomoon-add-box icon-btn"></i>
      </el-button>
    </div>
    <div v-if="!hiddenSelectedHM" class="hm-list">
      <div v-for="(hm, index) in selectHmList" class="hm" :key="index">
        <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="hm.email">
          <div class="email hm-info">{{ hm.email }}</div>
        </el-tooltip>
        <div class="first-name hm-info">{{ hm.first_name }}</div>
        <div class="last-name hm-info">{{ hm.last_name }}</div>
        <el-button class="delete-hm-btn hm-info" type="text" @click="handleDeleteHire(hm, index)">
          <i class="icomoon-remove-box icon-btn"></i>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { emailRegexp } from '@/utils/validator'

export default {
  props: {
    applicationList: {
      required: true
    },
    defaultSelectedHmList: {
      type: Array,
      required: false
    },
    hiddenLoading: {
      type: Boolean,
      default: false
    },
    hiddenSelectedHM: {
      type: Boolean,
      default: false
    },
    hiddenHeaderMessage: {
      type: Boolean,
      default: false
    },
    canSelectAll: {
      type: Boolean,
      default: false
    },
    onlySelectOne: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      hmEmailList: [],
      selectHmList: [],
      hmId: null,
      hmEmail: null,
      hmFirstName: null,
      hmLastName: null,
      emailError: null,
      firstNameError: null,
      selectHmError: null,
      loading: false,
      searchKeyword: null,
      disabledHmList: [],
      ignoreValid: false,
      selectAllHmList: []
    }
  },
  computed: {
    isSelectHmEmail () {
      if (!this.hmEmail) {
        return false
      }
      return this.hmEmail && _.some(this.hmEmailList, { email_address: this.hmEmail })
    },
    filterHmEmailList () {
      if (this.searchKeyword) {
        const filterList = _.filter(this.hmEmailList, item => {
          return _.includes(_.toUpper(item.email_address), _.toUpper(this.searchKeyword)) && !item.isSelected
        })
        if (filterList.length > 0 && this.canSelectAll) {
          filterList.splice(0, 0, {
            email_address: this.$t(`${this.i18nPrefix}.btn.selectAll`),
            id: 'all',
            value: 'all',
            hiddenStatus: true
          })
        }
        return filterList
      }
      return this.hmEmailList
    },
    disableAddHm () {
      return this.onlySelectOne && this.selectHmList.length >= 1
    }
  },
  created () {

  },
  methods: {
    ...mapActions('hm', ['getHmListForInvite']),
    ...mapGetters('account', ['getCurrentAccount']),
    visibleChange (visible) {
      if (!visible) {
        this.searchKeyword = null
      }
    },
    filterMethod (keyword) {
      this.searchKeyword = keyword
    },
    async loadHmEmailList () {
      this.loading = true
      try {
        const resp = await this.getHmListForInvite({
          job_id: _.chain(this.applicationList).map(item => item.job.id).uniq().join(',').value()
        })
        if (resp) {
          this.hmEmailList = _.map(resp.hm_list, hmInfo => {
            return {
              ...hmInfo,
              isSelected: false
            }
          })
          this.disabledHmList = resp.disabled_hm_list
        }
        if (this.defaultSelectedHmList) {
          this.selectHmList = _.map(this.defaultSelectedHmList, hm => {
            return {
              ...hm
            }
          })
        }
      } finally {
        this.loading = false
      }
    },
    validateHmInfo () {
      this.emailError = null
      this.firstNameError = null
      this.selectHmError = null

      if (this.ignoreValid) {
        return true
      }

      const isSelected = _.some(this.selectHmList, { email: this.hmEmail })
      if (isSelected) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.emailExisted`))
        this.hmEmail = null
        this.hmFirstName = null
        this.hmLastName = null
        this.hmCompany = null
        this.hmId = null
        return false
      }
      const selectDisabledHm = _.find(this.disabledHmList, disabledHm => {
        if (this.hmEmail === disabledHm.email_address) {
          return disabledHm
        }
      })
      if (selectDisabledHm) {
        this.selectHmError = this.$t('common.message.selectDisabledHmWarning')
      }
      if (!emailRegexp.test(this.hmEmail)) {
        this.emailError = this.$t('common.message.invalidEmail')
      }
      if (!this.hmFirstName) {
        this.firstNameError = this.$t('common.message.emptyName')
      }

      if (this.emailError || this.firstNameError || this.selectHmError) {
        return false
      }
      return true
    },
    handleAddHire () {
      if (!this.hmEmail || this.disableAddHm) {
        return
      }
      const isValid = this.validateHmInfo()
      if (!isValid) {
        return false
      }
      if (this.hmEmail === 'all') {
        _.forEach(this.selectAllHmList, item => this.handleAddOneHm(item.email_address, item.first_name, item.last_name, item.company, item.id))
      } else {
        this.handleAddOneHm(this.hmEmail, this.hmFirstName, this.hmLastName, this.hmCompany, this.hmId)
      }
      this.searchKeyword = null
      this.hmEmail = null
      this.hmFirstName = null
      this.hmLastName = null
      this.hmCompany = null
      this.hmId = null
      this.$emit('changeHmList', this.selectHmList)
      this.emailError = null
      this.firstNameError = null
      this.selectHmError = null
      return true
    },
    handleAddOneHm (email, firstName, lastName, company, hmId) {
      const hmInfo = {
        id: hmId,
        email: email,
        first_name: firstName,
        last_name: lastName,
        company: company
      }
      this.selectHmList.push(hmInfo)
      const hm = _.find(this.hmEmailList, { email_address: email })
      if (hm) {
        hm.isSelected = true
      }
    },
    handleSelectCurrentAccount () {
      const account = this.getCurrentAccount()
      const accountEmail = _.toLower(_.trim(account.email_address))
      const companyHm = _.find(this.hmEmailList, hm => _.toLower(_.trim(hm.email_address)) === accountEmail)
      this.hmEmail = companyHm ? companyHm.email_address : account.email_address
      this.hmFirstName = companyHm ? companyHm.first_name : account.first_name
      this.hmLastName = companyHm ? companyHm.last_name : account.last_name
      this.hmCompany = companyHm ? companyHm.company.id : account.company_id
      this.hmId = companyHm ? companyHm.id : null
      this.handleAddHire()
    },
    handleSelectHm (val) {
      this.ignoreValid = false
      if (val === 'all') {
        this.ignoreValid = true
        this.selectAllHmList = _.filter(this.filterHmEmailList, hmInfo => hmInfo.value !== 'all')
      }
      if (!this.isSelectHmEmail) {
        this.selectHm = {
          email_address: val,
          first_name: null,
          last_name: null,
          isSelected: false
        }
        this.hmFirstName = null
        this.hmLastName = null
        return
      }
      const hm = _.find(this.hmEmailList, hire => hire.email_address === val)
      this.selectHm = val
      this.hmFirstName = hm.first_name
      this.hmLastName = hm.last_name
      this.hmCompany = hm.company.id
      this.hmId = hm.id

      this.emailError = null
      this.firstNameError = null
      this.selectHmError = null
      this.$emit('selectHm', hm)
    },
    handleDeleteHire (hm, index) {
      this.selectHmList.splice(index, 1)

      const hmInfo = _.find(this.hmEmailList, { email_address: hm.email })
      if (hmInfo) {
        hmInfo.isSelected = false
      }
      this.$emit('changeHmList', this.selectHmList)
    },
    handleNextClick () {
      if (this.hmEmail && this.hmFirstName) {
        const isAddSuccess = this.handleAddHire()
        if (!isAddSuccess) {
          return false
        }
      }
      if (this.selectHmList.length < 1) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.hmRequired`))
        return false
      }
      this.hmEmail = null
      this.hmFirstName = null
      this.hmLastName = null
      this.hmId = null
      this.emailError = null
      this.firstNameError = null
      this.selectHmError = null
      return true
    },
    async refreshForm (initSelectHmList) {
      this.loading = true
      await this.$nextTick()
      this.hmEmailList = []
      this.selectHmList = []
      this.hmEmail = null
      this.hmFirstName = null
      this.hmLastName = null
      this.hmId = null
      this.emailError = null
      this.firstNameError = null
      this.selectHmError = null
      await this.loadHmEmailList()
      _.forEach(initSelectHmList, hmInfo => {
        const hm = _.find(this.hmEmailList, { email_address: hmInfo.email })
        if (hm) {
          hm.isSelected = true
        }
      })
      await this.$nextTick()
      this.loading = false
    }
  },
  watch: {
    hmEmail () {
      this.emailError = null
      this.firstNameError = null
      this.selectHmError = null
    }
  }
}
</script>

<style lang="scss" scoped>
.hm-selector-container {
  .selector-content {
    display: flex;
    margin-top: 16px;
    margin-bottom: 20px;

    .hm-email {
      position: relative;
      flex: 1;
      margin-right: 20px;
    }

    .hm-first-name {
      position: relative;
      flex: 1;
      margin-right: 20px;
    }

    .hm-last-name {
      flex: 1;
      margin-right: 20px;
    }
  }

  .has-hm-email-error {
    margin-bottom: 56px;
  }

  .hm-list {
    .hm {
      display: flex;
      align-items: center;

      .hm-info {
        flex: 1;
        margin-right: 20px;
        padding-left: 15px;

        &.email {
          word-break: keep-all;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &.delete-hm-btn {
          flex: none;
          margin-right: 0;
          padding-left: 0;
        }
      }

      .first-name {
        padding-left: 22px;
      }

      .last-name {
        padding-left: 22px;
      }
    }
  }

  .error {
    position: absolute;
    top: 100%;
    left: 0;
    padding-top: 4px;
    color: #f56c6c;
    font-size: 12px;
    line-height: 1;
  }

  .add-hm-btn, .delete-hm-btn {
    padding: 0;

    .icon-btn {
      font-size: 30px;

      &::before {
        color: #0096cc;
      }
    }

    &.is-disabled {
      .icon-btn {
        &::before {
          cursor: not-allowed;
          color: #c0c4cc;
        }
      }
    }
  }

  .message {
    font-size: 18px;
    font-weight: 500;
    color: #606266;
  }

  ::v-deep .el-input__inner {
    border: 1px solid #B1B5BD;
    border-radius: 2px;

    &:hover {
      border-color: #99a0ab;
    }
  }

  .el-input {
    &.is-disabled {
      ::v-deep {
        .el-input__inner {
          &:hover {
            border-color: #e4e7ed;
          }
        }
      }
    }
  }

}
</style>
